import * as Sentry from '@sentry/browser';
import { NextPageContext } from 'next';
import { validateError } from '@/utils/helpers';
import { apiCall } from '@/utils/api';
import { ApiErrorEnum } from '@/types/api';
import { IAttribute, ILintResult, IMapFeature } from '@/types/features';
import { IErrorValidation } from '@/types/helpers';
import {
  IAnnotationResponse,
  IBinaryResponse,
  IEnumeratorResponse,
  IMosaicResponse,
  IMergeResponse,
  IPointsResponse,
  ITrainersData,
  ITrainersTool,
  ITextAnnotation,
  ITrainersToolReponse,
  IPositionAzimuthResponse,
} from '@/types/trainers';
import { GetMapFeaturesInGeometryParams } from '@/types/deduplication';
import { FancyMultiSelect } from '@/components/shadcn/fancyselect';

// TODO: Consolidate API calls into this to cut down file size and redundant code.
export const performAPIGET = async (route: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}${route}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getReview = async (req?: NextPageContext['req']) => {
  const REVIEW_ROUTE = '/qa/review/consensus';
  try {
    const review = await apiCall(
      `${process.env.NEXT_PUBLIC_API}${REVIEW_ROUTE}`,
      {
        method: 'GET',
        headers: {
          cookie: req?.headers.cookie,
        },
      },
    );
    return review;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const submitReview = async (
  outcome: 'approved' | 'rejected',
  report: string,
) => {
  const SUBMIT_REVIEW_ROUTE = '/qa/review/consensus';
  try {
    const review = await apiCall(
      `${process.env.NEXT_PUBLIC_API}${SUBMIT_REVIEW_ROUTE}`,
      {
        method: 'POST',
      },
      { outcome, report },
    );
    return review;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const getReviewCount = async (req?: NextPageContext['req']) => {
  const REVIEW_COUNT_ROUTE = `/qa/review/count`;
  try {
    const count = await apiCall(
      `${process.env.NEXT_PUBLIC_API}${REVIEW_COUNT_ROUTE}`,
      {
        method: 'GET',
        headers: {
          cookie: req?.headers.cookie,
        },
      },
    );
    return count;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const getTools = async (
  req?: NextPageContext['req'],
): Promise<ITrainersToolReponse> => {
  const TOOLS_ROUTE = '/qa/tools';
  try {
    const response = await apiCall(
      `${process.env.NEXT_PUBLIC_API}${TOOLS_ROUTE}`,
      {
        method: 'GET',
        headers: {
          cookie: req?.headers.cookie,
        },
      },
    );

    return response;
  } catch (error: any) {
    console.log(error);
    return {
      tools: [],
      widgets: [],
      queues: [],
      locations: [],
      sources: [],
      categories: [],
      toolsToCachedRECounts: {},
      toolRenderOrder: {},
      accessMap: {},
    };
  }
};

// USE THIS SERVER SIDE ONLY
export const getTool = async (
  toolName: string,
  redirectToUrl: string,
  req: NextPageContext['req'],
) => {
  const redirectHome = {
    redirect: {
      destination: '/trainers',
      permanent: false,
    },
  };
  try {
    const { tools } = await getTools(req);
    const tool: ITrainersTool | undefined = (tools || [])?.find(
      (t: ITrainersTool) => t.name === toolName,
    );
    if (!tool || tool.disabled) {
      return redirectHome;
    }

    const data: ITrainersData = await getTrainersContent(
      tool._id,
      redirectToUrl,
      req,
    );
    if (data) {
      if (data.redirect)
        return { ...data }; // Important do not return other data for redirects
      else if (data.error) return { ...data, tool };
      else return { ...data, tools, tool };
    } else throw new Error();
  } catch (error) {
    console.log(error);
    return { error: 'something went wrong' };
  }
};

// Safe for client side.
export const getToolData = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/qa/tools/${id}`;
  try {
    const tool = await apiCall(url, {
      method: 'GET',
    });
    return tool;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getReviewableElementContent = async (id: string | undefined) => {
  if (!id) return { error: 'No reviewable element provided' };
  const url = `${process.env.NEXT_PUBLIC_API}/reviews/reviewableelement/${id}/content`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

interface ValidApiResponse {
  error?: string;
  content: any;
  count: number;
  toolId: string;
}

export const ignoreReviews = async (
  userId: string,
  username: string,
  req?: NextPageContext['req'],
) => {
  const URL = '/reviews/ignoreUser/' + userId;
  try {
    const data: any = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    alert('Reviews for ' + username + ' have been ignored successfully');
    return data;
  } catch (error: any) {
    Sentry.captureException(error, {
      tags: {
        section: 'qatool',
      },
    });
    Sentry.captureMessage(
      `Ignore reviews error: ${userId}`,
      error?.toString() || '',
    );

    console.log(error);
  }
};

export const getTrainersContent = async (
  toolId: string,
  redirectToUrl: string,
  req?: NextPageContext['req'],
): Promise<ValidApiResponse | IErrorValidation> => {
  try {
    const URL = `/qa/tools/${toolId}/content`;
    const data: ITrainersData = await apiCall(
      `${process.env.NEXT_PUBLIC_API}${URL}`,
      {
        method: 'GET',
        headers: {
          cookie: req?.headers.cookie,
        },
      },
    );
    const response: ValidApiResponse = {
      content: data?.content || null,
      count: data?.count || 0,
      toolId,
    };
    if (data?.error) {
      response.error = data.error;
    } else if (!response?.content) {
      response.error = ApiErrorEnum.NoContent;
    }
    return response;
  } catch (error: any) {
    console.log(error);
    let formattedError = validateError(error, redirectToUrl);
    if (!formattedError) {
      formattedError = { error: 'Something went wrong' };
    }
    return formattedError;
  }
};

type BodyType =
  | IAnnotationResponse
  | IBinaryResponse
  | IPointsResponse
  | IMosaicResponse
  | IMergeResponse
  | IEnumeratorResponse
  | IPositionAzimuthResponse;

export const submitTrainersResponse = async (
  toolId: string,
  body: BodyType,
) => {
  const URL = `/qa/tools/${toolId}/response`;
  try {
    return await apiCall(
      `${process.env.NEXT_PUBLIC_API}${URL}`,
      {
        method: 'POST',
      },
      { ...body },
    );
  } catch (error: any) {
    console.log(error);
    return { error: 'Something went wrong' };
  }
};

export const submitInvalidLandmark = async (landmarkId: string) => {
  const URL = `/admin/landmark/${landmarkId}/void`;

  try {
    return await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'POST',
    });
  } catch (error: any) {
    console.log(error);
    return { error: 'Something went wrong' };
  }
};

export const submitBenchmark = async (toolId: string, body: BodyType) => {
  const URL = `/qa/tools/${toolId}/benchmark`;
  try {
    return await apiCall(
      `${process.env.NEXT_PUBLIC_API}${URL}`,
      {
        method: 'POST',
      },
      { ...body },
    );
  } catch (error: any) {
    console.log(error);
  }
};

export const getGodModeFilters = async () => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/landmark/meta`;
  try {
    const res = await apiCall(url, {
      method: 'GET',
    });

    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getGodModeAudits = async () => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/landmark/audits`;
  try {
    const res = await apiCall(url, {
      method: 'GET',
    });

    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getValveAudits = async () => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapFeatures/audits`;
  try {
    const res = await apiCall(url, {
      method: 'GET',
    });

    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const submitGodModeAudit = async (id: any, auditPassed: boolean) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/landmark/${id}/audit`;
  try {
    const res = await apiCall(
      url,
      {
        method: 'POST',
      },
      { auditPassed },
    );

    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const submitValveAudit = async (id: any, auditPassed: boolean) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapFeatures/${id}/audit`;
  try {
    const res = await apiCall(
      url,
      {
        method: 'POST',
      },
      { auditPassed },
    );
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getPrevMapFeature = async (id: any) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapFeatures/${id}/getPRevWithSameLabelAndRegion`;
  try {
    const res = await apiCall(url, {
      method: 'GET',
    });
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getNextMapFeature = async (id: any) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapFeatures/${id}/getNextWithSameLabelAndRegion`;
  try {
    const res = await apiCall(url, {
      method: 'GET',
    });
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getTopPlayersForToolData = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/trainer_leaderboard/${id}`;
  try {
    const mapFeature = await apiCall(url, {
      method: 'GET',
    });
    return mapFeature;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getUserTrainerUsageData = async (username: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/user_trainer_usage/${username}`;
  try {
    const userTrainerUsageData = await apiCall(url, {
      method: 'GET',
    });
    return userTrainerUsageData;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getConsensusReview = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/reviews/consensusreview/${id}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getAdminReview = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/reviews/adminreview/${id}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getProposedBenchmark = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/reviews/proposedbenchmark/${id}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getProposedBenchmarkContent = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/reviews/proposedbenchmark/${id}/content`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getMapFeature = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapfeatures/${id}`;
  try {
    const mapFeature = await apiCall(url, {
      method: 'GET',
    });
    return mapFeature;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getMapFeaturesInGeometry = async (
  {
    limit,
    skip,
    orderBy,
    order,
    filterBy,
    geojson,
  }: GetMapFeaturesInGeometryParams,
  req?: NextPageContext['req'],
) => {
  const params = [
    ['limit', limit ? `${limit}` : undefined],
    ['skip', skip ? `${skip}` : undefined],
    ['sortBy', orderBy ? `${orderBy}` : undefined],
    ['ascending', `${order === 'asc'}`],
    ['filterBy', filterBy ? JSON.stringify(filterBy) : undefined],
  ];

  const queryParams = params
    .filter(param => param[1])
    .map(param => param.join(':'))
    .join('&');
  const URL = `${process.env.NEXT_PUBLIC_API}/mapFeatures/retrieveMapFeaturesInGeometry?${queryParams}`;

  try {
    const options = {
      method: 'POST',
      ...(req ? { headers: { cookie: req?.headers.cookie } } : {}),
    };
    const response = await apiCall(URL, options, {
      geojson,
    });
    return response;
  } catch (error: any) {
    console.error(error);
    return null;
  }
};

export const getMapFeatureHistory = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapfeatures/${id}/history`;
  try {
    const mapFeatureHistory = await apiCall(url, {
      method: 'GET',
    });
    return mapFeatureHistory;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getMapFeatureAttributes = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapfeatures/${id}/attributes`;
  try {
    const mapFeatureAttributes = await apiCall(url, {
      method: 'GET',
    });
    return mapFeatureAttributes;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getLandmarksAssets = async (mapFeatureId: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapfeatures/${mapFeatureId}/landmarksAssets`;
  try {
    const landmarksAssets = await apiCall(url, {
      method: 'GET',
    });
    return landmarksAssets;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getLandmarkAssets = async (landmarkId: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/landmarks/${landmarkId}/landmarkAssets`;
  try {
    const landmarkAssets = await apiCall(url, {
      method: 'GET',
    });
    return landmarkAssets;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getClassTaxonomy = async () => {
  const url = `${process.env.NEXT_PUBLIC_API}/taxonomy/classes`;
  try {
    const classTaxonomy = await apiCall(url, {
      method: 'GET',
    });
    return classTaxonomy;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getSubClassTaxonomy = async () => {
  const url = `${process.env.NEXT_PUBLIC_API}/taxonomy/subclasses`;
  try {
    const subClassTaxonomy = await apiCall(url, {
      method: 'GET',
    });
    return subClassTaxonomy;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getLintResultsForMapFeatureData = async (
  id: string,
): Promise<ILintResult[] | any> => {
  const lintResultsURL = `${process.env.NEXT_PUBLIC_API}/mapfeatures/${id}/lintresults`;
  try {
    const lintResults = await apiCall(lintResultsURL, { method: 'GET' });
    return lintResults;
  } catch (error: any) {
    return { error };
  }
};

export const getLandmarkMetadata = async (id: any) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/landmark/${id}/assets`;
  try {
    const res = await apiCall(url, {
      method: 'GET',
    });

    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export type OrderByType = 'createdAt' | 'label' | 'auditPassed' | '';

export const createMapFeatureFromLandmark = async (landmarkId: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/landmarks/${landmarkId}/createMapFeature`;
  try {
    const res = await apiCall(url, {
      method: 'POST',
    });
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const voidLandmark = async (landmarkId: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/landmarks/${landmarkId}/void`;
  try {
    const res = await apiCall(url, {
      method: 'POST',
    });
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const relabelLandmark = async (landmarkId: string, newLabel: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/landmarks/${landmarkId}/relabel`;
  try {
    const res = await apiCall(
      url,
      {
        method: 'POST',
      },
      { newLabel },
    );
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const voidMapFeature = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapFeatures/${id}/markVoid`;
  try {
    const res = await apiCall(url, {
      method: 'POST',
    });
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const mergeMapFeaturesByIds = async (featureIds: string[]) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapFeatures/merge`;
  try {
    const res = await apiCall(
      url,
      {
        method: 'POST',
      },
      { featureIds },
    );
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const deleteProposedBenchmark = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/qa/proposedBenchmark/${id}`;
  try {
    const res = await apiCall(url, {
      method: 'DELETE',
    });
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const voidConsensus = async (leaseId: string, toolId: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/consensus/void`;
  try {
    const res = await apiCall(
      url,
      {
        method: 'POST',
      },
      { leaseId, toolId },
    );
    return res;
  } catch (error: any) {
    return { error };
  }
};

export const redoMapFeature = async (mapFeatureId: string, tool: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapfeatures/${mapFeatureId}/redo`;
  try {
    const res = await apiCall(
      url,
      {
        method: 'POST',
      },
      { tool },
    );
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const fixMapFeatureAttribute = async (
  mapFeatureId: string,
  tool: string,
  annotations: ITextAnnotation[],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapfeatures/${mapFeatureId}/${tool}/overwrite`;
  try {
    const res = await apiCall(
      url,
      {
        method: 'PUT',
      },
      { annotations },
    );
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const addMapFeatureAttribute = async (
  mapFeatureId: string,
  attributes: Partial<IAttribute>[],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapfeatures/${mapFeatureId}/attributes`;
  try {
    const res = await apiCall(
      url,
      {
        method: 'POST',
      },
      { attributes },
    );
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const deleteMapFeatureAttribute = async (
  mapFeatureId: string,
  attributeId: string,
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapfeatures/${mapFeatureId}/attributes/${attributeId}/void`;
  try {
    const res = await apiCall(url, {
      method: 'PUT',
    });
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const updateMapFeaturePosition = async (
  mapFeatureId: string,
  lat: number,
  lng: number,
  azimuth?: number,
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapfeatures/${mapFeatureId}/position`;
  try {
    const res = await apiCall(
      url,
      {
        method: 'POST',
      },
      { lat, lng, ...(azimuth && { azimuth }) },
    );
    return res;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getLandmarkTools = async () => {
  const url = `${process.env.NEXT_PUBLIC_API}/qa/landmarkTools`;
  try {
    const tools = await apiCall(url, {
      method: 'GET',
    });
    return tools;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getProposedBenchmarksForTool = async (
  tool: string,
  req?: NextPageContext['req'],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/proposedBenchmarksForTool/${tool}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getConsensusReviewsForTool = async (
  tool: string,
  req?: NextPageContext['req'],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/consensusReviewsForTool/${tool}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getAdminReviewsForTool = async (
  tool: string,
  req?: NextPageContext['req'],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/reviews/adminReviewsForTool/${tool}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getLatestTrainerReviews = async () => {
  const url = `${process.env.NEXT_PUBLIC_API}/qa/latestReviews`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response?.latestReviews;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getPerformanceStats = async (
  tool: string,
  req?: NextPageContext['req'],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/qa/performance/${tool}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getUserPerformanceStats = async (
  user: string,
  limit: number,
  req?: NextPageContext['req'],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/qa/benchmarkhistory/${user}?limit=${limit}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getBenchmarkResponses = async (
  userId: string,
  benchmarkId: string,
  req?: NextPageContext['req'],
) => {
  if (!userId) return { error: 'No user provided' };
  if (!benchmarkId) return { error: 'No benchmarkId provided' };
  const url = `${process.env.NEXT_PUBLIC_API}/qa/benchmarkResponse/${userId}/${benchmarkId}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getUserReviewCounts = async (
  user: string,
  req?: NextPageContext['req'],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/qa/reviewcounts/${user}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};
// TODO: All of these API calls are the same func copy-pasted with slightly different URLs and params... we should consolidate.
export const getReviewableElement = async (
  id: string,
  req?: NextPageContext['req'],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/reviews/reviewableelement/${id}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getMapFeatureIcons = async () => {
  const url = `${process.env.NEXT_PUBLIC_API}/mapFeatures/icons`;

  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};
